import type { FC, ReactNode } from 'react'
import type { Faq } from '@/types'

import { useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'

interface FaqIndexProps {
    items: Faq[]
    /* Namespaced so two indexes can share a page without colliding ids. */
    idPrefix?: string
    className?: string
}

/* One numbered index, shared by every page that answers questions, so the home
   page and the contact page can never drift into two different treatments.
   Colours read from `currentColor`, which lets it sit on the near-black or the
   off-white without a variant. */
export const FaqIndex: FC<FaqIndexProps> = ({
    items,
    idPrefix = 'faq',
    className
}): ReactNode => {
    const scope = useRef<HTMLUListElement>(null)
    const panels = useRef<(HTMLDivElement | null)[]>([])
    const [open, setOpen] = useState<number | null>(0)

    useGsap(() => {
        const root = scope.current

        if (!root || prefersReducedMotion()) {
            return
        }

        gsap.fromTo(root.querySelectorAll('[data-faq-rule]'),
            { scaleX: 0 },
            {
                scaleX: 1,
                duration: 1.1,
                stagger: 0.07,
                transformOrigin: 'left',
                ease: 'noma',
                scrollTrigger: { trigger: root, start: 'top 86%', once: true }
            }
        )

        gsap.fromTo(root.querySelectorAll('[data-faq-head]'),
            { opacity: 0, y: 24 },
            {
                opacity: 1,
                y: 0,
                duration: 0.95,
                stagger: 0.06,
                ease: 'noma',
                scrollTrigger: { trigger: root, start: 'top 86%', once: true }
            }
        )
    }, scope, [])

    /* Measured to its exact height then released to auto, so a row can reflow
       if the viewport changes while it is open. */
    useGsap(() => {
        panels.current.forEach((panel, index) => {
            if (!panel) {
                return
            }

            const active = open === index

            gsap.to(panel, {
                height: active ? panel.scrollHeight : 0,
                opacity: active ? 1 : 0,
                duration: 0.6,
                ease: 'noma-soft',
                onComplete: () => {
                    if (active) {
                        gsap.set(panel, { height: 'auto' })
                    }
                }
            })
        })
    }, scope, [open])

    return (
        <ul ref={scope} data-faq-list className={cn('flex flex-col', className)}>
            {items.map((item, index) => {
                const active = open === index

                return (
                    <li key={item.question}>
                        <span
                            data-faq-rule
                            aria-hidden='true'
                            className='block h-px w-full origin-left bg-current opacity-15'
                        />

                        <h3>
                            <button
                                type='button'
                                data-faq-head
                                data-cursor='link'
                                aria-expanded={active}
                                aria-controls={`${idPrefix}-panel-${index}`}
                                id={`${idPrefix}-trigger-${index}`}
                                onClick={() => setOpen(active ? null : index)}
                                className='group/faq grid w-full grid-cols-[auto_1fr_auto] items-start gap-4 py-5 text-left transition-[padding-left] duration-[700ms] ease-[cubic-bezier(0.16,1,0.3,1)] lg:gap-8 lg:py-7 lg:group-hover/faq:pl-2'
                            >
                                <span
                                    className={cn(
                                        't-mono nums mt-[0.45em] transition-colors duration-500',
                                        active ? 'text-brand' : 'opacity-55'
                                    )}
                                >
                                    {String(index + 1).padStart(2, '0')}
                                </span>

                                <span
                                    className={cn(
                                        't-display text-[clamp(19px,2.2vw,30px)] leading-[1.1] transition-colors duration-500',
                                        active ? 'text-brand' : 'group-hover/faq:text-brand'
                                    )}
                                >
                                    {item.question}
                                </span>

                                <span
                                    aria-hidden='true'
                                    className='relative mt-[0.35em] flex size-5 shrink-0 items-center justify-center'
                                >
                                    <span className='absolute h-px w-4 bg-current' />

                                    <span
                                        className={cn(
                                            'absolute h-4 w-px bg-current transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]',
                                            active && 'scale-y-0'
                                        )}
                                    />
                                </span>
                            </button>
                        </h3>

                        <div
                            id={`${idPrefix}-panel-${index}`}
                            role='region'
                            aria-labelledby={`${idPrefix}-trigger-${index}`}
                            ref={(node) => {
                                panels.current[index] = node
                            }}
                            className='h-0 overflow-hidden opacity-0'
                        >
                            <div className='grid grid-cols-[auto_1fr] gap-4 pb-7 lg:gap-8'>
                                <span aria-hidden='true' className='t-mono invisible'>
                                    00
                                </span>

                                <p className='t-cond-soft max-w-[68ch] text-[15px] leading-[1.6] opacity-70'>
                                    {item.answer}
                                </p>
                            </div>
                        </div>
                    </li>
                )
            })}

            <span
                data-faq-rule
                aria-hidden='true'
                className='block h-px w-full origin-left bg-current opacity-15'
            />
        </ul>
    )
}
